"use client";

import React, { useState } from 'react';
import SidebarNav from './SidebarNav';
import Breadcrumbs from './Breadcrumbs';
import OnThisPage from './OnThisPage';
import Footer from './Footer';
import { navSections, NavItem } from '@/lib/navData';

export interface ApiLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  description?: string;
  activeLink: 'overview' | 'authentication' | 'forecast' | 'realtime' | 'recent-history' | 'history' | 'timelines' | 'core-data-fields' | 'data-layers' | 'use-cases' | 'weather-maps' | 'weather-on-routes' | 'error-handling' | 'historical-data-fields' | 'quick-start' | 'access-keys' | 'rate-limiting' | 'versioning' | 'climate-normals' | 'weather-data-layers' | 'locations-api' | 'insights-api' | 'alerts-api' | 'events-api' | 'notifications-api' | 'severe-weather-events' | 'platform-integration' | 'developer-resources' | 'reference' | 'weather-codes' | 'data-layers-overview' | 'best-practices' | 'integration-patterns' | 'tools-libraries' | 'webhooks' | 'changelog' | 'weather-forecast' | 'realtime-weather' | 'weather-timelines' | 'historical-weather' | 'historical-climate-normals' | 'on-demand-events-api' | 'custom-events-api';
  showOnThisPage?: boolean;
  customBreadcrumbs?: Array<{ label: string; href?: string }>;
  showSidebar?: boolean; // Allow explicit control over sidebar visibility
}

export default function ApiLayout({
  children,
  title,
  subtitle,
  description,
  activeLink,
  showOnThisPage = true,
  customBreadcrumbs,
  showSidebar: explicitShowSidebar,
}: ApiLayoutProps) {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(false);

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const toggleDesktopSidebar = () => {
    setIsDesktopSidebarCollapsed(!isDesktopSidebarCollapsed);
  };

  // Show sidebar by default on all pages except overview (homepage)
  // Can be overridden with explicit showSidebar prop
  const showSidebar = explicitShowSidebar !== undefined 
    ? explicitShowSidebar 
    : activeLink !== 'overview';

  // Get current zone name based on activeLink (same logic as SidebarNav)
  const getCurrentZoneName = () => {
    // Define zone mappings
    const getStartedZonePages = [
      'overview', 'quick-start', 'access-keys', 'rate-limiting', 'webhooks', 
      'versioning', 'formats', 'pagination', 'error-handling', 'tools-libraries', 'changelog'
    ];

    const weatherLayersZonePages = [
      'core-data-fields', 'historical-data-fields', 'weather-codes', 'data-layers', 'weather-data-layers'
    ];

    const weatherForecastZonePages = [
      'weather-forecast', 'realtime-weather', 'weather-timelines', 'weather-on-routes'
    ];

    const historicalApiZonePages = [
      'recent-history', 'historical-weather', 'climate-normals', 'historical-climate-normals'
    ];

    const monitoringZonePages = [
      'locations-api', 'insights-api', 'alerts-api', 'events-api', 'custom-events-api', 
      'on-demand-events-api', 'severe-weather-events', 'notifications-api', 'flight-schedule', 
      'stations-data', 'scheduled-events', 'platform-integration', 'platform-data-integration'
    ];

    const weatherMapZonePages = ['weather-maps'];

    // Determine zone based on activeLink
    if (getStartedZonePages.includes(activeLink || '')) {
      return 'Get Started';
    }
    if (weatherLayersZonePages.includes(activeLink || '')) {
      return 'Weather Data Layers';
    }
    if (weatherForecastZonePages.includes(activeLink || '')) {
      return 'Weather Forecast & Realtime';
    }
    if (historicalApiZonePages.includes(activeLink || '')) {
      return 'Historical Weather';
    }
    if (monitoringZonePages.includes(activeLink || '')) {
      return 'Monitoring API';
    }
    if (weatherMapZonePages.includes(activeLink || '')) {
      return 'Weather Maps';
    }

    // Default fallback
    return 'Navigation';
  };

  const currentZoneName = getCurrentZoneName();

  return (
    <>
      {/* Main content container following Next.js docs pattern */}
      <div className="relative mx-auto max-w-full py-10 px-4 md:px-0 md:flex md:flex-row md:py-10">
        {/* Mobile sidebar toggle button */}
        {showSidebar && (
          <button
            onClick={toggleMobileSidebar}
            className="sidenav-toggle md:hidden mb-4 w-full flex items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {currentZoneName}
            <svg fill="none" viewBox="0 0 24 24" className="Icon Icon3_D2ysxFZ_ll Icon-svg2Lm7f6G9Ly5a h-[15px]" data-name="chevron-up-down" role="img" ><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="m6 16 6 6 6-6M18 8l-6-6-6 6" className="icon-stroke-width"></path></svg>
          </button>
        )}

        {/* Mobile sidebar overlay */}
        {showSidebar && isMobileSidebarOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={toggleMobileSidebar}></div>
            <div className="fixed top-0 left-0 h-full w-80 bg-white shadow-lg overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">Navigation</h2>
                <button
                  onClick={toggleMobileSidebar}
                  className="p-2 rounded-md hover:bg-gray-100"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4">
                <SidebarNav activeLink={activeLink} />
              </div>
            </div>
          </div>
        )}
        
        {/* Left Sidebar - Fixed width, sticky positioned */}
        {showSidebar && (
          <div className={`sticky top-[121px] hidden h-[calc(100vh-121px)] md:flex md:shrink-0 md:flex-col md:justify-between transition-all duration-300 ease-in-out ${
            isDesktopSidebarCollapsed ? 'w-12' : 'w-[284px]'
          }`}>
            <div className="overflow-hidden relative">
              {/* Desktop Sidebar Toggle Button */}
              <div className="flex items-center justify-between mb-4 px-2">
                {!isDesktopSidebarCollapsed && (
                  <span className="text-sm font-medium text-gray-600">Navigation</span>
                )}
                <button
                  onClick={toggleDesktopSidebar}
                  className="p-2 rounded-md hover:bg-gray-100 transition-colors duration-200"
                  title={isDesktopSidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
                >
                  <svg 
                    className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${
                      isDesktopSidebarCollapsed ? 'rotate-180' : ''
                    }`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
              </div>
              
              {/* Sidebar Content */}
              <div className={`transition-all duration-300 ease-in-out ${
                isDesktopSidebarCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'
              }`}>
                <SidebarNav activeLink={activeLink} />
              </div>
            </div>
          </div>
        )}
        
        {/* Right Sidebar - On This Page - Fixed width, sticky positioned */}
        {showOnThisPage && (
          <nav className="order-last hidden w-56 shrink-0 lg:block">
            <div className="sticky top-[126px] h-[calc(100vh-121px)]">
              <OnThisPage />
            </div>
          </nav>
        )}
        
        {/* Main Article Content - Flexible width, minimum width to prevent overflow */}
        <article className={`mt-4 w-full min-w-0 max-w-6xl px-1 md:px-6 transition-all duration-300 ease-in-out ${
          showSidebar && isDesktopSidebarCollapsed ? 'md:ml-4' : ''
        }`} style={{minHeight: 'calc(-103px + 100vh)'}}>
          {/* Breadcrumbs */}
          <Breadcrumbs activeLink={activeLink} customBreadcrumbs={customBreadcrumbs} />
          
          {/* Page header */}
          <section className="page-header pb-12">
            <div className="mx-auto max-w-full">
              <h1>{title}</h1>
              {subtitle && <h2 className="text-3xl mt-6 mb-4 text-gray-700">{subtitle}</h2>}
              {/* {description && <p className="text-xl text-[#666666] max-w-4xl leading-relaxed">{description}</p>} */}
            </div>
          </section>
          
          {/* Main content with prose styling */}
          <div className="prose prose-vercel max-w-none" data-docs="true">
            {children}
          </div>
        </article>
      </div>
      
      <Footer />
    </>
  );
} 